import { inject, Injectable } from '@angular/core';
import { QueryParamsBuilder } from 'features/compliance-dashboard/services/query-params-builder';
import { ShowIfHttpError } from '@certemy/common';
import { Observable } from 'rxjs';
import {
  REGISTRY_DEPENDENCY_FILTERS,
  RegistryDependencyCreatePayload,
  RegistryDependencyDefaultFilters,
  RegistryDependencyFilters,
  RegistryDependencyNames,
  RegistryDependencyPotentialCertifications,
  RegistryDependencyPotentialProfile,
  RegistryDependencyQueryFilters,
  RegistryDependencySmartFiltersValues,
  RegistryDependencyTableData,
  RegistryDependencyValidationResponse,
} from 'features/organization-settings/registry/registry-dependency/registry-dependency.constant';
import { HttpService } from 'services/http/http.service';
import { WorkflowListFilter } from 'features/workflow-module/workflow-module.constants';
import { map, take } from 'rxjs/operators';
import { mapToQueryParams } from 'utils/helpers';
import _orderBy from 'lodash-es/orderBy';
import { HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';

@Injectable()
export class RegistryDependencyService extends QueryParamsBuilder {
  constructor(private http: HttpService) {
    super();
  }

  @ShowIfHttpError('An error occurred while receiving registry dependency data.')
  getRegistryDependencyData(filters: RegistryDependencyFilters): Observable<any> {
    const queryFilters = this.tableFiltersToQueryFilters(filters);
    const queryParamsString = mapToQueryParams<RegistryDependencyQueryFilters>({
      filters: queryFilters,
      createOffset: true,
    });
    return this.http.get(`/registry-dependency${queryParamsString}`);
  }

  @ShowIfHttpError('Cannot receive filter.')
  getFilterList(
    filterTypeId: REGISTRY_DEPENDENCY_FILTERS,
    filter: WorkflowListFilter<RegistryDependencySmartFiltersValues>,
  ): Observable<any> {
    const queryParams = this.getQueryParams(filter);
    return this.http.get(`/registry-dependency/filter-data/${filterTypeId}${queryParams}`);
  }

  @ShowIfHttpError('An error occurred while receiving a list by dependency name.')
  getDependencyName(search: string): Observable<RegistryDependencyNames[]> {
    return this.http.get(`/registry-dependency/names?search=${search}`).pipe(
      map(options => {
        const sorter = option => option.dependencyName.toLowerCase();
        return _orderBy(options, [sorter], ['asc']);
      }),
    );
  }

  @ShowIfHttpError('An error occurred while receiving a list of dependents.')
  getActiveProfile(search: string): Observable<RegistryDependencyPotentialProfile[]> {
    return this.http.get(`/registry-dependency/potential/profiles?search=${search}`).pipe(
      map(options => {
        const firstNameSorter = option => option.firstName.toLowerCase();
        const lastNameSorter = option => option.lastName.toLowerCase();
        return _orderBy(options, [firstNameSorter, lastNameSorter], ['asc', 'asc']);
      }),
    );
  }

  @ShowIfHttpError('An error occurred while receiving a list of registry records.')
  getRegistryRecordByProfile(
    profileId: number,
    search: string,
  ): Observable<RegistryDependencyPotentialCertifications[]> {
    return this.http.get(`/registry-dependency/potential/profiles/${profileId}/certifications?search=${search}`).pipe(
      map(options => {
        const credentialNameSorter = option => (option.credentialName || '').toLowerCase();
        const acronymNameSorter = option => (option.acronym || '').toLowerCase();
        return _orderBy(options, [credentialNameSorter, acronymNameSorter], ['asc', 'asc']);
      }),
    );
  }

  @ShowIfHttpError('An error occurred while creating dependency record.')
  createRegistryRecord(payload: RegistryDependencyCreatePayload): Observable<void> {
    return this.http.post('/registry-dependency', payload);
  }

  @ShowIfHttpError('An error occurred while creating dependency record.')
  validateBeforeCreatingRegistryRecord(
    payload: RegistryDependencyCreatePayload,
  ): Observable<RegistryDependencyValidationResponse> {
    return this.http.post('/registry-dependency/validate', payload);
  }

  @ShowIfHttpError('An error occurred while deactivating dependency.')
  deactivateDependency(dependencyIds: number[]): Observable<RegistryDependencyValidationResponse> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: { dependencyIds },
    };
    return this.http.delete('/registry-dependency', options);
  }

  private tableFiltersToQueryFilters({
    order,
    ...restFilters
  }: RegistryDependencyFilters): RegistryDependencyQueryFilters {
    return {
      ...restFilters,
      orderId: order.header.propertyName || order.header.value,
      orderType: order.order,
    };
  }
}

@Injectable()
export class RegistryDependencyResolverService  {
  constructor(private registryDependencyService: RegistryDependencyService) {}

  resolve(): Observable<RegistryDependencyTableData> {
    return this.registryDependencyService
      .getRegistryDependencyData(new RegistryDependencyDefaultFilters())
      .pipe(take(1));
  }
}

export const registryDependencyResolver: ResolveFn<Observable<RegistryDependencyTableData>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(RegistryDependencyResolverService).resolve();
};
